import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import FileUpload from '../../../components/ui/FileUpload';

const FloorTypesTab = ({ floorTypes, onAdd, onEdit, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    thumbnail: '',
    thumbnailAlt: '',
    priceTemplate: '',
    usageCount: 0
  });

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        description: '',
        thumbnail: '',
        thumbnailAlt: '',
        priceTemplate: '',
        usageCount: 0
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (editingItem) {
      onEdit({ ...formData, id: editingItem?.id });
    } else {
      onAdd({ ...formData, id: Date.now() });
    }
    handleCloseModal();
  };

  const handleDelete = (item) => {
    if (window.confirm(`Delete "${item?.name}"? This will affect ${item?.usageCount} companies.`)) {
      onDelete(item?.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Floor Types</h2>
          <p className="text-sm text-text-secondary mt-1">
            Manage default floor type options available to all companies
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => handleOpenModal()}
          iconName="Plus"
          iconPosition="left"
          iconSize={16}
        >
          Add Floor Type
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {floorTypes?.map((type) => (
          <div
            key={type?.id}
            className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200"
          >
            <div className="aspect-video bg-muted overflow-hidden">
              <Image
                src={type?.thumbnail}
                alt={type?.thumbnailAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4 space-y-3">
              <div>
                <h3 className="font-semibold text-text-primary">{type?.name}</h3>
                <p className="text-sm text-text-secondary mt-1 line-clamp-2">
                  {type?.description}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <Icon name="Building2" size={14} />
                <span>Used by {type?.usageCount} companies</span>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenModal(type)}
                  iconName="Edit2"
                  iconPosition="left"
                  iconSize={14}
                  className="flex-1"
                >
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(type)}
                  iconName="Trash2"
                  iconSize={14}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-primary">
                {editingItem ? 'Edit Floor Type' : 'Add Floor Type'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-text-secondary hover:text-text-primary transition-colors duration-150"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Input
                label="Floor Type Name"
                type="text"
                placeholder="e.g., Metallic Epoxy"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                required
              />

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-ring"
                  rows="3"
                  placeholder="Describe this floor type..."
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  required
                />
              </div>

              <FileUpload
                label="Thumbnail Image"
                accept="image/*"
                onUpload={(file) => {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setFormData({
                      ...formData,
                      thumbnail: reader?.result,
                      thumbnailAlt: `${formData?.name} floor type showing texture and finish details`
                    });
                  };
                  reader?.readAsDataURL(file);
                }}
              />

              <Input
                label="Price Template"
                type="text"
                placeholder="e.g., $8-12 per sq ft"
                value={formData?.priceTemplate}
                onChange={(e) => setFormData({ ...formData, priceTemplate: e?.target?.value })}
                description="Default pricing guidance for companies"
              />

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" variant="default" className="flex-1">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorTypesTab;